import { CommonModule } from '@angular/common';
import { Component, OnInit, computed, signal, inject } from '@angular/core';
import { StiApiService } from 'src/app/services/sti/sti-api.service';
import { StiAuthService } from 'src/app/services/sti/sti-auth.service';
import { PureSpinnerComponent } from '../../pure-spinner/pure-spinner.component';
import { ToastrDialogService } from 'src/app/services/toastr-dialog.service';
import { ToastrActionEnum } from 'src/app/utils/enums/toastr.enum';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { StiBook } from 'src/app/services/sti/sti.models';
import { FileOverviewTableColumnName, FileOverviewTableComponent, FileOverviewTableRow } from 'src/app/shared/ui/file-overview-table/file-overview-table.component';
import { OpenFileAction, OpenFileService } from 'src/app/services/open-file.service';
import { TabService } from 'src/app/services/tab.service';
import { Subscription, filter, skip, take } from 'rxjs';
import { RemoveExtensionStringPipe } from 'src/app/utils/pipes/remove-extension-string.pipe';
@Component({
  selector: 'asg-sti-dialog',
  templateUrl: './sti-dialog.component.html',
  styleUrls: ['./sti-dialog.component.scss'],
  standalone: true,
  imports: [CommonModule, PureSpinnerComponent, MatDialogModule, MatButtonModule, TranslateModule, FileOverviewTableComponent]
})
export class StiDialogComponent implements OnInit {
  public isLoading = signal(false);
  public isOpeningFile = signal(false);
  public books = signal<StiBook[]>([]);
  public error = signal<string | null>(null);
  public selectedFileId = signal<string | null>(null);
  public isFileSelected = computed(() => !!this.selectedFileId());
  public viewMode = signal<'books' | 'files'>('books');
  public columnNames = signal<FileOverviewTableColumnName[]>([]);
  public tableRows = signal<FileOverviewTableRow[]>([]);

  private readonly stiApiService = inject(StiApiService);
  private readonly stiAuthService = inject(StiAuthService);
  private readonly toastrService = inject(ToastrDialogService);
  private readonly translateService = inject(TranslateService);
  private readonly dialogRef = inject(MatDialogRef<StiDialogComponent>);
  private readonly openFileService = inject(OpenFileService);
  private readonly tabService = inject(TabService);
  private readonly removeExtension = inject(RemoveExtensionStringPipe);

  async ngOnInit(): Promise<void> {
    this.isLoading.set(true);
    const isAuthorized = await this.checkAuthorize();
    if (isAuthorized) await this.fetchBooks();
  }

  public async fetchBooks(): Promise<void> {
    this.error.set(null);
    this.isLoading.set(true);
    try {
      const books = await this.stiApiService.getBooksForCurrentUser();
      this.books.set(books);
      this.setAllBooks();
    } catch (error) {
      console.error('STI books request failed: ', error);
      this.error.set('STI_DIALOG.FAILED_BOOKS');
    } finally {
      this.isLoading.set(false);
    }
  }

  public onMetadataSelected(id: string): void {
    if (this.viewMode() === 'books') {
      this.setSpecificBook(id);
      return;
    }

    this.selectedFileId.set(id);
  }

  public onHomeClick(): void {
    this.error.set(null);
    this.setAllBooks();
  }

  public onCancelClick(): void {
    this.dialogRef.close();
  }

  public async onOpenClick(): Promise<void> {
    if (this.isOpeningFile()) {
      return;
    }

    const selectedFileId = this.selectedFileId();
    if (!selectedFileId) {
      console.error('No file selected to open.');
      return;
    }

    this.isOpeningFile.set(true);
    let activeTabSubscription: Subscription | undefined;
    try {
      const file = await this.getBookFile(selectedFileId);

      activeTabSubscription = this.tabService.activeTabObservable()
      .pipe(
        skip(1), // skip the first emission which is the current active tab
        filter((activeTab) => this.removeExtension.transform(activeTab?.title || '') === this.removeExtension.transform(file.name)),
        take(1)
      ).subscribe(() => {
        this.tabService.setActiveTabAsSTISource();
      });

      await this.openFileService.open(file, OpenFileAction.OPEN);
      this.dialogRef.close();
    } catch(error) {
      console.error('Failed to open STI book file: ', error);
      activeTabSubscription?.unsubscribe();
      this.toastrService.showToaster(ToastrActionEnum.Error, this.translateService.instant('STI_DIALOG.FILE_NOT_FOUND'));
    } finally {
      this.isOpeningFile.set(false);
    }
  }

  public onCloseClick(): void {
    this.dialogRef.close();
  }

  private async checkAuthorize(): Promise<boolean> {
    try {
      if (!this.stiAuthService.getToken()) {
        await this.stiAuthService.authorize();
      }
      return true;
    } catch (error) {
      console.error('STI authorization failed: ', error);
      this.toastrService.showToaster(ToastrActionEnum.Error, this.translateService.instant('STI_DIALOG.FAILED_AUTH'));
      this.dialogRef.close();
      return false;
    }
  }

  private setAllBooks(): void {
    this.viewMode.set('books');
    this.selectedFileId.set(null);
    this.tableRows.set(this.books().map((book) => ({
      id: book.metadata.id,
      icon: 'fas fa-folder',
      name: book.metadata.title,
      course: book.metadata.course,
    })));
    this.columnNames.set(['icon', 'name', 'course']);
  }

  private setSpecificBook(id: string): void {
    const book = this.books().find((book) => book.metadata.id === id);
    if (!book) {
      console.error(`Book with id ${id} not found.`);
      this.error.set('STI_DIALOG.BOOK_NOT_FOUND');
      return;
    }

    this.viewMode.set('files');
    this.selectedFileId.set(null);
    this.tableRows.set(book.files.map((file) => ({
      id: file.id,
      icon: file.thumb ?? 'fas fa-file',
      name: file.name,
    })));
    this.columnNames.set(['icon', 'name']);
  }

  private async getBookFile(fileId: string): Promise<File> {
    const bookFile = this.books().flatMap((book) => book.files).find((file) => file.id === fileId);
    if (!bookFile) {
      throw new Error(`File with id ${fileId} not found.`);
    }

    return this.stiApiService.getBookFile(fileId);
  }
}